// The sky as an observer sees it, from the atmosphere's tables (atmospherePhysics.ts): ONE table of what the air sends to the eye along every direction, whatever the eye's height. No three.js
// here, so it can be run and checked on its own (the test builds it with esbuild and holds it to the reader's direct answer).
//
// What it is. For an observer at height h above the planet and the star at elevation `se` at the eye, the table holds, for every direction of the whole sphere, the radiance the air
// sends to the eye (single scattering marched through the air the world has, the rest from the tables), as D = pi x radiance per unit of the star's irradiance outside the air (a colour:
// the table carries the star's own). The picture's sky is flux x D, where `flux` is the star's irradiance outside the air; with the sky an irradiance E on open ground, D is what the old
// dome called "E x shape". Nothing in it is tied to a place or to the ground: climbing is just a different h, and the same table holds the sky from the ground (a dome of light above a
// horizon at the eye's level), from ten kilometres (the horizon has dropped below the eye's level, and the air under it is a bright haze), and from space (a thin bright limb over
// the dark of the planet, and nothing above it).
//
// Layout. Rows run over elevation from straight down to straight up, laid out from the TRUE horizon (the ray that grazes the planet, at elevation elH = asin(mu_h(r)), which is 0 on the
// ground and about -20 degrees at 400 km) with the row spacing the square root of the distance from it on both sides, so the horizon, where the sky changes fastest, has the finest rows;
// the horizon itself falls between two rows, so the step across it (sky above, ground below) is never interpolated over. Columns run over the azimuth from the star's vertical plane, 0
// to pi (the sky is symmetric about that plane). A second, one-dimensional table holds the transmittance of the view ray to where it ends (the top of the air, or the ground), by row only:
// it does not depend on where the star is.
//
// The same table gives, on the CPU, what the rest of the picture needs of it: the irradiance it implies on open ground, how concentrated the light is (the exposure's twilight term), the
// colour of the sky just above the horizon (what distant land fades to), and the shape (a mean of 1) the ambient light is made from.
import type { Atmosphere, Medium } from './atmospherePhysics';

export const SV_NA = 32;      // azimuth columns, 0..pi from the star
export const SV_NV = 48;      // rows over the whole sphere of elevations
export const SV_STEPS = 20;   // steps of a ray's march (atmospherePhysics.ts single); the table is smooth, a ray costs about 4 us at 20
const SV_T_STEPS = 24;        // steps of a row's transmittance
const HALF_PI = Math.PI / 2;
const DOME_NM = 32, DOME_NP = 24;                      // the sampling of the upper hemisphere for the derived numbers: equal steps in the sine of the elevation (equal solid angle), azimuth over 0..pi
const HORIZON_ABOVE = 1.5 * Math.PI / 180;             // the colour distant land fades to is the sky this far above the horizon, round the compass

/** The elevation (rad) of row coordinate v in [0, 1], for a table whose true horizon is at elevation elH (<= 0). */
export function svEl(v: number, elH: number): number {
  const t = 2 * v - 1;
  return t >= 0 ? elH + t * t * (HALF_PI - elH) : elH - t * t * (HALF_PI + elH);
}

/** The row coordinate of an elevation: the inverse of svEl. */
export function svV(el: number, elH: number): number {
  const up = el - elH;
  const t = up >= 0 ? Math.sqrt(up / Math.max(HALF_PI - elH, 1e-6)) : -Math.sqrt(-up / Math.max(HALF_PI + elH, 1e-6));
  return 0.5 + 0.5 * t;
}

const lum = (r: number, g: number, b: number) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

export class SkyView {
  /** D = pi x radiance, per unit star irradiance outside the air: [row][column][3], of the last finished solve. */
  readonly rad = new Float32Array(SV_NV * SV_NA * 3);
  /** The view ray's transmittance to where it ends: [row][3]. */
  readonly tr = new Float32Array(SV_NV * 3);
  /** The elevation of the true horizon the finished table was laid out for (rad, <= 0). */
  elH = 0;
  /** The observer's height (m) the finished table is for. */
  height = 0;
  /** The star's elevation at the eye (rad) the finished table is for. */
  starEl = HALF_PI;
  /** What the table implies for open ground, per unit star irradiance outside: pi times the cosine-weighted mean radiance of the upper hemisphere, a colour (the sky's irradiance on a horizontal surface, which the tables' esky holds as well). */
  readonly irradiance: [number, number, number] = [0, 0, 0];
  /** The colour of the sky a degree and a half above the true horizon, round the compass, as D: what distant land fades to. */
  readonly horizon: [number, number, number] = [0, 0, 0];
  /** Root mean square luminance of the upper hemisphere over the cosine-weighted mean (1 for a flat sky; a glow at the horizon makes it large). */
  glowRms = 1;

  private pend = new Float32Array(SV_NV * SV_NA * 3);
  private pendTr = new Float32Array(SV_NV * 3);
  private pendElH = 0;
  private pendH = 0;
  private pendStar = HALF_PI;
  private readonly o: number[] = [0, 0, 0];

  /** Start a solve for an eye `h` metres up: the rows are laid out for its horizon. The finished table stays in use until `finish`. */
  begin(A: Atmosphere, h: number): void {
    this.pendH = Math.max(0, h);
    this.pendElH = Math.asin(Math.max(-1, Math.min(0, A.horizonMu(this.pendH))));
  }

  /** Rows j0 up to (not including) j1 of the solve under way, for the star at elevation `se` at the eye and the air of `medium`. */
  fillRows(A: Atmosphere, se: number, medium: Medium, j0: number, j1: number): void {
    const h = this.pendH, elH = this.pendElH, mus = Math.sin(se), cs = Math.cos(se), o = this.o;
    this.pendStar = se;
    for (let j = j0; j < j1; j++) {
      const el = svEl((j + 0.5) / SV_NV, elH), sinEl = Math.sin(el), cosEl = Math.cos(el);
      A.viewTrans(h, sinEl, SV_T_STEPS, medium, o);
      this.pendTr[j * 3] = o[0]!; this.pendTr[j * 3 + 1] = o[1]!; this.pendTr[j * 3 + 2] = o[2]!;
      for (let i = 0; i < SV_NA; i++) {
        const phi = (i + 0.5) / SV_NA * Math.PI;
        A.radiance(h, sinEl, mus, cosEl * Math.cos(phi) * cs + sinEl * mus, SV_STEPS, medium, o);
        const n = (j * SV_NA + i) * 3;
        this.pend[n] = Math.max(o[0]!, 0) * Math.PI; this.pend[n + 1] = Math.max(o[1]!, 0) * Math.PI; this.pend[n + 2] = Math.max(o[2]!, 0) * Math.PI;
      }
    }
  }

  /** Put the finished rows in use and work out the numbers derived from them. */
  finish(): void {
    this.rad.set(this.pend); this.tr.set(this.pendTr);
    this.elH = this.pendElH; this.height = this.pendH; this.starEl = this.pendStar;
    // the upper hemisphere (above the horizontal at the eye, not above the true horizon): the cosine-weighted mean sets the irradiance, the equal-solid-angle mean of the square the glow
    let wsum = 0, n = 0, s2 = 0;
    let e0 = 0, e1 = 0, e2 = 0, lc = 0;
    const o = this.o;
    for (let k = 0; k < DOME_NM; k++) {
      const mu = (k + 0.5) / DOME_NM;
      for (let l = 0; l < DOME_NP; l++) {
        this.valueAt(mu, (l + 0.5) / DOME_NP * Math.PI, o);
        const L = lum(o[0]!, o[1]!, o[2]!);
        e0 += mu * o[0]!; e1 += mu * o[1]!; e2 += mu * o[2]!; wsum += mu; lc += mu * L;
        s2 += L * L; n++;
      }
    }
    this.irradiance[0] = e0 / wsum; this.irradiance[1] = e1 / wsum; this.irradiance[2] = e2 / wsum;
    const meanL = lc / wsum;
    this.glowRms = meanL > 1e-30 ? Math.sqrt(s2 / n) / meanL : 1;
    // the colour above the horizon
    const el = Math.min(HALF_PI, this.elH + HORIZON_ABOVE), sinEl = Math.sin(el);
    let h0 = 0, h1 = 0, h2 = 0;
    for (let i = 0; i < SV_NA; i++) {
      this.valueAt(sinEl, (i + 0.5) / SV_NA * Math.PI, o);
      h0 += o[0]!; h1 += o[1]!; h2 += o[2]!;
    }
    this.horizon[0] = h0 / SV_NA; this.horizon[1] = h1 / SV_NA; this.horizon[2] = h2 / SV_NA;
  }

  /** The whole solve in one call (for the first one, and for tests). */
  solve(A: Atmosphere, h: number, se: number, medium: Medium): void {
    this.begin(A, h);
    this.fillRows(A, se, medium, 0, SV_NV);
    this.finish();
  }

  /** D (a colour) for the direction with sine of elevation `sinEl` and azimuth `phi` (0..pi) from the star, interpolated in the finished table, into `out`. */
  valueAt(sinEl: number, phi: number, out: number[] | Float64Array): void {
    const el = Math.asin(Math.max(-1, Math.min(1, sinEl)));
    const fj = Math.min(SV_NV - 1, Math.max(0, svV(el, this.elH) * SV_NV - 0.5));
    const fi = Math.min(SV_NA - 1, Math.max(0, phi / Math.PI * SV_NA - 0.5));
    const j0 = fj | 0, i0 = fi | 0, j1 = Math.min(SV_NV - 1, j0 + 1), i1 = Math.min(SV_NA - 1, i0 + 1), tj = fj - j0, ti = fi - i0;
    const T = this.rad;
    for (let c = 0; c < 3; c++) {
      const a = T[(j0 * SV_NA + i0) * 3 + c]!, b = T[(j0 * SV_NA + i1) * 3 + c]!, d = T[(j1 * SV_NA + i0) * 3 + c]!, e = T[(j1 * SV_NA + i1) * 3 + c]!;
      const lo = a + (b - a) * ti;
      out[c] = lo + ((d + (e - d) * ti) - lo) * tj;
    }
  }

  /** The view ray's transmittance for the direction with sine of elevation `sinEl` (a colour), from the finished table. */
  transAt(sinEl: number, out: number[] | Float64Array): void {
    const el = Math.asin(Math.max(-1, Math.min(1, sinEl)));
    const fj = Math.min(SV_NV - 1, Math.max(0, svV(el, this.elH) * SV_NV - 0.5));
    const j0 = fj | 0, j1 = Math.min(SV_NV - 1, j0 + 1), tj = fj - j0;
    for (let c = 0; c < 3; c++) out[c] = this.tr[j0 * 3 + c]! + (this.tr[j1 * 3 + c]! - this.tr[j0 * 3 + c]!) * tj;
  }

  /** The sky's shape for the direction (sine of elevation, azimuth from the star): D over the table's own irradiance, a colour with a cosine-weighted mean of 1 over the upper hemisphere (all 1 when the table is dark). */
  shapeAt(sinEl: number, phi: number, out: number[] | Float64Array): void {
    this.valueAt(sinEl, phi, out);
    for (let c = 0; c < 3; c++) out[c] = this.irradiance[c]! > 1e-12 ? out[c]! / this.irradiance[c]! : 1;
  }
}
